import React from "react";
import { getIn } from "formik";
import * as Api from "./Api";
import { UploadItem } from "@vwfs-bronson/bronson-react";
import { formatFileSize } from "./formatFileSize";
import PropTypes from "prop-types";
import * as Output from "../../Text";

import { UploadItemState } from "../Polyfill";
import { Analytics } from '../../services/analytics';

class DocumentUploadItem extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      progress: 0,
      done: true,
    };
  }

  defaultTrackingData = {
    [Analytics.keys.pageName()]: 'Document upload',
    [Analytics.keys.viewChange()]: 'Document upload',
  }

  trackUploadFailed = (errorMessage) => {
    const failedData = {
      [Analytics.keys.errorMessage()]: errorMessage,
      [Analytics.keys.eventType(0)]: 'interAction',
      [Analytics.keys.eventAction(0)]: 'Success',
      [Analytics.keys.linkInformation(0)]: 'Document upload failed',
    }
    Analytics.setTrackingData({...this.defaultTrackingData, ...failedData})
    Analytics.trackInteraction()
  }

  componentDidMount() {
    if (!this.props.file.state) {
      this.handleFilePromise =
        this.handleFileAsync().then(() => {
          const {
            file: { state, error },
          } = this.props;
          const isError = state === UploadItemState.ERROR;
          if (isError) {
            this.trackUploadFailed(error)
          }
        })
    }
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      prevProps.file.state !== UploadItemState.SUCCESS &&
      this.props.file.state === UploadItemState.SUCCESS
    ) {
      setTimeout(() => {
        this.setState({ done: true });
      }, 1000);
    }
  }

  render() {
    const {
      transactionId,
      file: { name, size, state, error, id },
      handleDelete,
    } = this.props;

    return (
      <UploadItem
        fileName={name}
        message={error}
        error={error}
        hideMainButton={true}
        onSecondaryButtonClick={() => {
          if (state === UploadItemState.SUCCESS) {
            Api.deleteAsync({ fileId: id, transactionId }).catch((e) => {
              console.error("File deletion failed!");
            });
          }

          if (handleDelete) {
            handleDelete();
          }
          // todo cancel file upload?!
        }}
        loading={state === UploadItemState.LOADING}
        fileSize={size && formatFileSize(size)} // X of
      />
    );
  }

  setFieldValue(update) {
    const {
      form: { values, setFieldValue },
      name,
    } = this.props;

    const value = getIn(values, name);
    setFieldValue(name, { ...value, ...update });
  }

  async handleFileAsync() {
    const { file, form, rootName, transactionId, setisUploading, maxFiles } = this.props;
    const files = getIn(form.values, rootName);

    if (!file.bytes) {
      this.setFieldValue({
        state: UploadItemState.ERROR,
        error: Output.resolve("generic.errors.D003"),
      });
      setisUploading(false);
      return;
    }

    if (
      files.filter((f) => f.state !== UploadItemState.ERROR).length >
      maxFiles
    ) {
      this.setFieldValue({
        state: UploadItemState.ERROR,
        error: Output.resolve("generic.errors.D006"),
      });
      setisUploading(false);
      return;
    }

    if (file.size > 1000 * 1000 * 4.5) {
      this.setFieldValue({
        state: UploadItemState.ERROR,
        error: Output.resolve("generic.errors.D002"),
      });
      setisUploading(false);
      return;
    }

    if (!["application/pdf", "image/jpeg"].includes(file.type)) {
      this.setFieldValue({
        state: UploadItemState.ERROR,
        error: Output.resolve("generic.errors.D001"),
      });
      setisUploading(false);
      return;
    }

    this.setFieldValue({ state: UploadItemState.LOADING });
    this.setState({ progress: 0, done: false });
    setisUploading(true)
    try {
      const { id, status } = await Api.uploadAsync({
        file: {
          name: file.name,
          mimeType: file.type,
          base64: file.bytes,
          tags: ["uploads", rootName],
          expirationDays: 14,
        },
        transactionId,
        onUploadProgress: ({ loaded, total }) =>
          this.setState({ progress: total ? loaded / total : 0 }),
      });

      switch (status) {
        case 200:
          this.setFieldValue({
            state: UploadItemState.SUCCESS,
            error: undefined,
            id,
          });
          break;
        case 400:
          this.setFieldValue({
            state: UploadItemState.ERROR,
            error: Output.resolve("generic.errors.D001"),
          });
          break;
        default:
          this.setFieldValue({
            state: UploadItemState.ERROR,
            error: Output.resolve("generic.errors.D003"),
          });
      }
    } catch (e) {
      console.error(e);
      setisUploading(false);

      this.setFieldValue({
        state: UploadItemState.ERROR,
        error: Output.resolve("generic.errors.D003"),
      });
    } finally {
      setisUploading(false);
    }
  }
}

DocumentUploadItem.propTypes = {
  transactionId: PropTypes.string.isRequired,
  file: PropTypes.shape({
    name: PropTypes.string.isRequired,
    size: PropTypes.number.isRequired,
    bytes: PropTypes.any.isRequired,
    state: PropTypes.oneOf(Object.values(UploadItemState)),
  }).isRequired,
  handleDelete: PropTypes.func,
  maxFiles: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  rootName: PropTypes.string.isRequired,
  form: PropTypes.object.isRequired,
};

export { DocumentUploadItem };
